import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMail, FiUnlock, FiCheckCircle, FiShield, FiX, FiLoader } = FiIcons;

function PremiumModal({ isOpen, onClose, onProceed, resolution }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  // Determine price based on resolution
  const price = resolution === '2K' ? '49' : '99';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    await onProceed(email);
    setLoading(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
          >
            <div className="relative p-6 text-center">
              <button 
                onClick={onClose}
                className="absolute top-4 right-4 p-2 hover:bg-slate-100 rounded-full transition-colors"
                disabled={loading}
              >
                <SafeIcon icon={FiX} className="w-5 h-5 text-slate-400" />
              </button>

              <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiUnlock} className="w-8 h-8 text-indigo-600" />
              </div>

              <h3 className="text-2xl font-bold text-slate-900 mb-2">Unlock {resolution} Quality</h3>
              <p className="text-slate-500 mb-6">Get your poster in crystal clear {resolution} resolution for professional printing.</p>

              <div className="space-y-3 mb-8">
                {[
                  `Crystal clear ${resolution} resolution`,
                  'No compression artifacts',
                  'Perfect for professional prints',
                  'Global payment support (UPI, Card, etc.)'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3 text-left">
                    <div className="shrink-0 w-5 h-5 rounded-full bg-green-100 flex items-center justify-center">
                      <SafeIcon icon={FiCheckCircle} className="w-3 h-3 text-green-600" />
                    </div>
                    <span className="text-sm font-medium text-slate-600">{item}</span>
                  </div>
                ))}
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <SafeIcon icon={FiMail} className="w-4 h-4 text-slate-400" />
                  </div>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter email for delivery"
                    className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 shadow-lg shadow-indigo-100 transition-all disabled:opacity-70"
                >
                  {loading ? (
                    <SafeIcon icon={FiLoader} className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <span>Pay ₹{price} to Unlock</span>
                      <SafeIcon icon={FiShield} className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
              <p className="mt-4 text-[11px] text-slate-400">
                Secure payment via Lemon Squeezy. Supports UPI & Cards.
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

export default PremiumModal;